"use client";

import React from "react";
import { ShieldCheck, ArrowRight, FileText, CircleCheck, SlidersHorizontal } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface HeroCopy {
  categoryBadge: string;
  standardBadge: string;
  heading: string;
  lead: string;
  guideLabel: string;
  imageAlt: string;
  chipLabel: string;
  cardNote: string;
  cardLink: string;
  strip: Array<{
    label: string;
    value: string;
  }>;
}
const copyData: Record<Lang, HeroCopy> = {
  en: {
    categoryBadge: "Stainless Steel & Alloys",
    standardBadge: "ASTM E2016 / ISO 9044",
    heading: "Stainless steel and specialty alloys for precision filter mesh.",
    lead: "Select verified industrial stainless steel grades and corrosion-resistant alloys for woven wire cloth and sintered filter media. Chemical composition verification and conditional material test certificates available upon order specification.",
    guideLabel: "Material Comparison",
    imageAlt: "Stainless steel and specialty alloys for industrial filtration wire mesh",
    chipLabel: "AISI 304, 304L, 316, 316L",
    cardNote: "Conditional PMI & MTR available on request",
    cardLink: "Grade Matrix",
    strip: [{
      label: "Woven Wire Mesh Range",
      value: "1 µm – 25 mm"
    }, {
      label: "Wire Diameters",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Standard Alloys",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Compliance Standards",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  zh: {
    categoryBadge: "不锈钢与合金材料",
    standardBadge: "ASTM E2016 / ISO 9044",
    heading: "用于精密过滤丝网的不锈钢及特种合金材料",
    lead: "选用经过验证的工业标准不锈钢牌号与耐腐蚀合金，适用于编织丝网与烧结过滤元件。可按订单要求提供化学成分验证与材质证明书。",
    guideLabel: "材料选型对比",
    imageAlt: "用于工业过滤丝网的不锈钢及特种合金材料",
    chipLabel: "AISI 304、304L、316、316L",
    cardNote: "可按订单要求提供 PMI 与 MTR",
    cardLink: "牌号矩阵",
    strip: [{
      label: "编织丝网过滤范围",
      value: "1 µm – 25 mm"
    }, {
      label: "加工丝径范围",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "常备合金牌号",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "执行制造标准",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  es: {
    categoryBadge: "Acero inoxidable y aleaciones",
    standardBadge: "ASTM E2016 / ISO 9044",
    heading: "Acero inoxidable y aleaciones especiales para mallas de filtración.",
    lead: "Seleccione calidades industriales de acero inoxidable y aleaciones resistentes a la corrosión para mallas tejidas y medios sinterizados. Verificación química y certificados de prueba de material disponibles según especificación del pedido.",
    guideLabel: "Comparativa de materiales",
    imageAlt: "Acero inoxidable y aleaciones especiales para mallas de filtración industrial",
    chipLabel: "AISI 304, 304L, 316, 316L",
    cardNote: "PMI y MTR disponibles bajo pedido",
    cardLink: "Matriz de grados",
    strip: [{
      label: "Rango de malla tejida",
      value: "1 µm – 25 mm"
    }, {
      label: "Diámetros de alambre",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Aleaciones estándar",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Normas de fabricación",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  de: {
    categoryBadge: "Edelstahl & Legierungen",
    standardBadge: "ASTM E2016 / ISO 9044",
    heading: "Edelstahl und Speziallegierungen für Präzisions-Filtergewebe.",
    lead: "Wählen Sie bewährte Industrie-Edelstahlsorten und korrosionsbeständige Legierungen für Drahtgewebe und Sinterfiltermedien. Chemische Analyse und Materialprüfzeugnisse auf Auftragsanforderung verfügbar.",
    guideLabel: "Werkstoffvergleich",
    imageAlt: "Edelstahl und Speziallegierungen für industrielle Filtergewebe",
    chipLabel: "AISI 304, 304L, 316, 316L",
    cardNote: "PMI und MTR auf Bestellanforderung erhältlich",
    cardLink: "Werkstoffmatrix",
    strip: [{
      label: "Drahtgewebe-Bereich",
      value: "1 µm – 25 mm"
    }, {
      label: "Drahtdurchmesser",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Standard-Legierungen",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Fertigungsnormen",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  fr: {
    categoryBadge: "Acier inoxydable et alliages",
    standardBadge: "ASTM E2016 / ISO 9044",
    heading: "Acier inoxydable et alliages spéciaux pour toiles de filtration de précision.",
    lead: "Sélectionnez des nuances industrielles d'acier inoxydable et d'alliages résistants à la corrosion pour toiles tissées et médias frittés. Contrôle de composition chimique et certificats matière disponibles sur demande de commande.",
    guideLabel: "Comparatif des matériaux",
    imageAlt: "Acier inoxydable et alliages spéciaux pour toiles de filtration industrielle",
    chipLabel: "AISI 304, 304L, 316, 316L",
    cardNote: "PMI et MTR disponibles sur demande",
    cardLink: "Matrice des nuances",
    strip: [{
      label: "Plage toile tissée",
      value: "1 µm – 25 mm"
    }, {
      label: "Diamètres de fil",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Nuances standard",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Normes applicables",
      value: "ASTM E2016 / ISO 9044"
    }]
  },
  pt: {
    categoryBadge: "Aço inoxidável e ligas",
    standardBadge: "ASTM E2016 / ISO 9044",
    heading: "Aço inoxidável e ligas especiais para telas de filtragem de precisão.",
    lead: "Selecione graus industriais de aço inoxidável e ligas resistentes à corrosão para telas tecidas e meios filtrantes sinterizados. Verificação de composição química e certificados de ensaio disponíveis conforme especificação do pedido.",
    guideLabel: "Comparativo de materiais",
    imageAlt: "Aço inoxidável e ligas especiais para telas de filtragem industrial",
    chipLabel: "AISI 304, 304L, 316, 316L",
    cardNote: "PMI e MTR disponíveis sob consulta",
    cardLink: "Matriz de ligas",
    strip: [{
      label: "Faixa de tela tecida",
      value: "1 µm – 25 mm"
    }, {
      label: "Diâmetros de arame",
      value: "0.020 mm – 2.0 mm"
    }, {
      label: "Ligas padrão",
      value: "AISI 304 / 304L / 316 / 316L"
    }, {
      label: "Normas de fabricação",
      value: "ASTM E2016 / ISO 9044"
    }]
  }
};
export default function AlloyHero() {
  const {
    lang
  } = useLanguage();
  const copy = pick(lang, copyData);
  return <section id="alloyhero" data-nav="light" className="relative overflow-hidden bg-background mf-hero border-b border-border text-foreground">
      <div className="mf-container relative">
        {/* Main Hero Two-Column Grid */}
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-8">
          {/* Left Column (7 Columns on lg) */}
          <div className="lg:col-span-7 flex flex-col items-start">
            <Reveal>
              {/* Badges row: exactly two badges with outline variant */}
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
                  <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.categoryBadge}</span>
                </Badge>
                <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 font-mono mf-caption text-foreground">
                  <ShieldCheck className="size-3.5 text-primary" aria-hidden="true" />
                  <span>{copy.standardBadge}</span>
                </Badge>
              </div>

              {/* Main single h1 */}
              <h1 className="font-default text-balance mf-h1 text-foreground">
                {copy.heading}
              </h1>

              {/* Lead paragraph */}
              <p className="mt-4 max-w-2xl text-muted-foreground mf-lead">
                {copy.lead}
              </p>
            </Reveal>

            {/* CTAs */}
            <Reveal delay={120}>
              <div className="mt-6 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
                <Button size="lg" asChild className="px-6 font-semibold transition-colors">
                  <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>

                <Button variant="outline" size="lg" asChild className="px-6 font-semibold transition-colors">
                  <Link to="/MaterialsAndSurfaceFinishesPage" className="inline-flex items-center gap-2">
                    <FileText className="size-4" aria-hidden="true" />
                    <span>{copy.guideLabel}</span>
                  </Link>
                </Button>
              </div>
            </Reveal>
          </div>

          {/* Right Column: Spec / Visual Card (5 Columns on lg) */}
          <div className="lg:col-span-5 w-full">
            <Reveal delay={120}>
              <Card className="overflow-hidden rounded-lg border border-border bg-card gap-0 py-0">
                <div className="mf-card-media relative mf-ratio-hero w-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-photograph-of-several-stainless-st-f67b74.webp" alt={copy.imageAlt} loading="eager" fetchPriority="high" decoding="async" className="h-full w-full object-cover object-center" />
                  <div className="absolute top-3 left-3 bg-card border border-border px-3 py-1 rounded-md mf-caption font-mono text-foreground flex items-center gap-1.5">
                    <SlidersHorizontal className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{copy.chipLabel}</span>
                  </div>
                </div>

                <CardContent className="p-4">
                  <div className="flex items-center justify-between mf-caption">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <CircleCheck className="size-3.5 text-primary" aria-hidden="true" />
                      <span>{copy.cardNote}</span>
                    </div>
                    <Link to="/MaterialsAndGradesPage" className="font-medium text-primary hover:underline whitespace-nowrap ml-2 flex items-center gap-1 shrink-0">
                      <span>{copy.cardLink}</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </div>

        {/* Spec Strip below grid */}
        <div className="mf-spec-strip">
          {copy.strip.map((spec, idx) => <div key={idx} className="mf-spec-strip__item">
              <span className="mf-spec-strip__label">{spec.label}</span>
              <span className="mf-spec-strip__value">{spec.value}</span>
            </div>)}
        </div>
      </div>
    </section>;
}